// ISDP CUSTOM: pure helpers behind the two custom scaffolder fields (AuthorProfile, AdoRepoTarget).
// Kept free of React and Backstage APIs so every rule (slug, validation, ADO headers, response
// mapping, availability bookkeeping) is unit-testable on its own.
import { isValidEmail } from '../../common/validation';

// ---------------------------------------------------------------------------------------------
// AuthorProfile
// ---------------------------------------------------------------------------------------------

export interface AuthorProfileValue {
  name: string;
  email: string;
}

export type AuthorProfileError =
  | 'nameRequired'
  | 'emailRequired'
  | 'emailInvalid';

/** Every rule the AuthorProfile value breaks, in display order (empty = valid). */
export function authorProfileErrors(
  value: Partial<AuthorProfileValue> | undefined,
): AuthorProfileError[] {
  const errors: AuthorProfileError[] = [];
  const name = (value?.name ?? '').trim();
  const email = (value?.email ?? '').trim();
  if (!name) errors.push('nameRequired');
  if (!email) errors.push('emailRequired');
  else if (!isValidEmail(email)) errors.push('emailInvalid');
  return errors;
}

/** Prefill from the signed-in profile — only fills what the user hasn't typed yet. */
export function prefillAuthorProfile(
  current: Partial<AuthorProfileValue> | undefined,
  profile: { displayName?: string; email?: string } | undefined,
): AuthorProfileValue {
  return {
    name: current?.name || profile?.displayName || '',
    email: current?.email || profile?.email || '',
  };
}

// ---------------------------------------------------------------------------------------------
// DocTypePicker
// ---------------------------------------------------------------------------------------------

/** The starter kit's doc types, in display order, with their card icon (registered in index.tsx). */
export const DOC_TYPES = [
  { value: 'quick', known: 'Quick', icon: 'core::document' },
  { value: 'guide', known: 'Guide', icon: 'core::request-list' },
  { value: 'bilingual', known: 'Bilingual', icon: 'core::globe' },
] as const;

export interface DocTypeOption {
  value: string;
  /** i18n key stem (fields.docType<known>Title/Desc/Best) for a value the frontend knows. */
  known?: 'Quick' | 'Guide' | 'Bilingual';
  icon: string;
  /** Fallback title (schema enumNames, else the value) for a value the frontend doesn't know. */
  label: string;
}

/** The cards to show: the schema's enum (in its order) when it has one, else the three known types. */
export function docTypeOptions(
  schema: { enum?: unknown; enumNames?: unknown } | undefined,
): DocTypeOption[] {
  const values: string[] = Array.isArray(schema?.enum)
    ? (schema!.enum as unknown[]).filter(
        (v): v is string => typeof v === 'string',
      )
    : DOC_TYPES.map(d => d.value);
  const names: unknown[] = Array.isArray(schema?.enumNames)
    ? (schema!.enumNames as unknown[])
    : [];
  return values.map((value, i) => {
    const known = DOC_TYPES.find(d => d.value === value);
    const name = names[i];
    return {
      value,
      known: known?.known,
      icon: known?.icon ?? 'core::document',
      label: typeof name === 'string' ? name : value,
    };
  });
}

// ---------------------------------------------------------------------------------------------
// AdoRepoTarget
// ---------------------------------------------------------------------------------------------

export const ADO_ORG = 'draeger';
export const ADO_BASE = `https://dev.azure.com/${ADO_ORG}`;
export const ADO_API_VERSION = '7.1';
/** Secret key the PAT is stored under by the built-in `ui:field: Secret` on the same step. */
export const DEFAULT_PAT_SECRET = 'adoPat';
/** formData key the repo-name default is derived from. */
export const DEFAULT_TITLE_FIELD = 'title';

export interface AdoRepoTargetValue {
  project: string;
  repoName: string;
}

// Hyphen-separated lower-case words: no leading/trailing hyphen and no "--" (a trailing hyphen is an
// invalid Backstage entity name, and the repo name doubles as the catalog entity name). ≤ 63 chars.
export const REPO_NAME_RE = /^[a-z0-9]+(-[a-z0-9]+)*$/;
export const MAX_REPO_LEN = 63;
export const DOCS_SUFFIX = '-docs';

export const isValidRepoName = (name: string): boolean =>
  name.length <= MAX_REPO_LEN && REPO_NAME_RE.test(name);

const TRANSLIT: Record<string, string> = { ä: 'ae', ö: 'oe', ü: 'ue', ß: 'ss' };

/** Lower-case, ASCII-only, hyphen-separated slug. German umlauts are transliterated (ä→ae). */
export function slugify(input: string): string {
  return input
    .toLowerCase()
    .replace(/[äöüß]/g, ch => TRANSLIT[ch])
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

// ---- the "-docs" suffix ---------------------------------------------------------------------
// The user types only the BASE name; the field shows a fixed "-docs" suffix and stores the FINAL
// name. A base that already ends in "docs" ("manual-docs", "mydocs", "docs") is kept as is, so
// nobody ends up with "manual-docs-docs".

/** True when the base already carries the docs ending, so "-docs" is not appended again. */
export const baseHasDocsEnding = (base: string): boolean => /docs$/.test(base);

/** The final repository name for a typed base ('' stays ''). */
export function finalRepoName(base: string): string {
  if (!base) return '';
  return baseHasDocsEnding(base) ? base : `${base}${DOCS_SUFFIX}`;
}

/** The base to show in the input for a stored final name (inverse of finalRepoName). */
export function baseFromRepoName(name: string): string {
  return name.endsWith(DOCS_SUFFIX) && name.length > DOCS_SUFFIX.length
    ? name.slice(0, -DOCS_SUFFIX.length)
    : name;
}

/** Normalises what the user types into the base input: lower-case, whitespace/underscores → "-". */
export const normalizeBaseInput = (raw: string): string =>
  raw
    .toLowerCase()
    .replace(/[\s_]+/g, '-')
    .replace(/^-+/, '');

/** Default base: slug(title) with any trailing "docs" stripped, cut so base + "-docs" ≤ 63 chars. */
export function defaultRepoBase(title: string | undefined): string {
  const slug = slugify(title ?? '')
    .replace(/-?docs$/, '')
    .replace(/-+$/, '');
  return slug.slice(0, MAX_REPO_LEN - DOCS_SUFFIX.length).replace(/-+$/, '');
}

/** `<defaultRepoBase(title)>-docs`; '' when the title has no usable chars. */
export function defaultRepoName(title: string | undefined): string {
  const base = defaultRepoBase(title);
  return base ? `${base}${DOCS_SUFFIX}` : '';
}

/** Headers for a browser call to Azure DevOps with a PAT. The suppress header turns a bad-PAT
 *  302-to-login into a plain 401 the browser can read. */
export function adoHeaders(pat: string): Record<string, string> {
  return {
    Authorization: `Basic ${btoa(`:${pat}`)}`,
    'X-TFS-FedAuthRedirect': 'Suppress',
    Accept: 'application/json',
  };
}

export const adoProjectsUrl = () =>
  `${ADO_BASE}/_apis/projects?api-version=${ADO_API_VERSION}&$top=500`;

export const adoRepoUrl = (project: string, repoName: string) =>
  `${ADO_BASE}/${encodeURIComponent(
    project,
  )}/_apis/git/repositories/${encodeURIComponent(
    repoName,
  )}?api-version=${ADO_API_VERSION}`;

/** Project names from a `GET _apis/projects` body, sorted, de-duplicated, blanks dropped. */
export function mapProjectsResponse(body: unknown): string[] {
  const value = (body as { value?: unknown })?.value;
  if (!Array.isArray(value)) return [];
  const names = value
    .map(p =>
      p && typeof (p as { name?: unknown }).name === 'string'
        ? (p as { name: string }).name.trim()
        : '',
    )
    .filter(Boolean);
  return Array.from(new Set(names)).sort((a, b) => a.localeCompare(b));
}

export type AdoErrorKind = 'unauthorized' | 'network' | 'http';

/** Classify a failed ADO response. 401/403 (and 203, which ADO uses for a login page) = PAT problem. */
export function classifyAdoStatus(status: number): AdoErrorKind {
  return status === 401 || status === 403 || status === 203
    ? 'unauthorized'
    : 'http';
}

export type RepoAvailability =
  | { state: 'checking' }
  | { state: 'available' }
  | { state: 'taken'; where: 'ado' | 'catalog' }
  | { state: 'error'; kind: AdoErrorKind; status?: number };

/** Combine the ADO repo probe and the catalog lookup into one availability verdict. */
export function combineAvailability(
  adoStatus: number | 'network',
  catalogTaken: boolean,
): RepoAvailability {
  if (adoStatus === 200) return { state: 'taken', where: 'ado' };
  if (catalogTaken) return { state: 'taken', where: 'catalog' };
  if (adoStatus === 404) return { state: 'available' };
  if (adoStatus === 'network') return { state: 'error', kind: 'network' };
  return {
    state: 'error',
    kind: classifyAdoStatus(adoStatus),
    status: adoStatus,
  };
}

// The field component records each name's verdict here so the step validator (which runs on Next
// and has no access to the template secrets, hence no PAT) can block on it. Keyed by project+name
// only — the PAT never touches this store.
const availability = new Map<string, RepoAvailability>();
const key = (v: AdoRepoTargetValue) => `${v.project}\u0000${v.repoName}`;

export const recordAvailability = (
  v: AdoRepoTargetValue,
  a: RepoAvailability,
) => {
  availability.set(key(v), a);
};
export const lookupAvailability = (
  v: AdoRepoTargetValue,
): RepoAvailability | undefined => availability.get(key(v));
/** Test hook. */
export const resetAvailability = () => availability.clear();

export type AdoRepoTargetError =
  | 'projectRequired'
  | 'repoRequired'
  | 'repoPattern'
  | 'repoTakenAdo'
  | 'repoTakenCatalog'
  | 'repoChecking'
  | 'repoCheckFailed';

/** The synchronous (shape) rules for an AdoRepoTarget value. */
export function adoRepoTargetShapeErrors(
  value: Partial<AdoRepoTargetValue> | undefined,
): AdoRepoTargetError[] {
  const errors: AdoRepoTargetError[] = [];
  if (!value?.project) errors.push('projectRequired');
  const repo = value?.repoName ?? '';
  if (!repo) errors.push('repoRequired');
  else if (!isValidRepoName(repo)) errors.push('repoPattern');
  return errors;
}

/** Maps a recorded availability verdict to the blocking error (if any). */
export function availabilityError(
  a: RepoAvailability | undefined,
): AdoRepoTargetError | undefined {
  if (!a || a.state === 'checking') return 'repoChecking';
  if (a.state === 'taken')
    return a.where === 'ado' ? 'repoTakenAdo' : 'repoTakenCatalog';
  if (a.state === 'error') return 'repoCheckFailed';
  return undefined;
}

/** ServiceNow catalogue items for people who have no Azure DevOps access yet. */
export const ADO_LICENSE_ORDER_URL =
  'https://draeger.service-now.com/sp?id=sc_cat_item&table=sc_cat_item&sys_id=2126dcce1bb2ad54391942609b4bcb9a';
export const ADO_PROJECT_REQUEST_URL =
  'https://draeger.service-now.com/sp?id=sc_cat_item&table=sc_cat_item&sys_id=f4600eb31b64b300238ca9b7bd4bcb67';
